import { type ReactNode } from "react";

/**
 * AuthGate — Phase 1 (client-side presence check only).
 *
 * Cloudflare Access terminates auth in front of the Pages deployment. The
 * authoritative check is the Access JWT verification that will live in
 * `functions/_middleware.ts` in Phase 2 (reads `Cf-Access-Jwt-Assertion` and
 * validates against the team JWKS server-side).
 *
 * Here on the client we only confirm the `CF_Authorization` cookie is
 * *present* — no signature verification, no JWKS fetch. This acts as a soft
 * local gate that avoids rendering the UI when the document was served
 * without an Access session (e.g. a misconfigured route). When Access is
 * working, the cookie is set by the edge.
 *
 * Note: if Cloudflare Access is configured to mark `CF_Authorization` as
 * HttpOnly (default for some account tiers), `document.cookie` will never
 * surface it and this check will always fail. That is fine — Phase 2
 * middleware is the real gate; if needed we'll relax this to a passthrough
 * after confirming the cookie visibility in the deployed environment.
 *
 * Deliberately no `import.meta.env.DEV` bypass (spec §2.7).
 */
export function AuthGate({ children }: { children: ReactNode }) {
  if (!hasAccessCookie()) {
    return null;
  }
  return <>{children}</>;
}

function hasAccessCookie(): boolean {
  if (typeof document === "undefined") return false;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  return cookies.some((c) => c.startsWith("CF_Authorization="));
}
